import { groupBy, uniq } from 'lodash'

import { SchedulingRuleViolation } from '@bemlo/schedule'
import { Enumerable } from '@bemlo/utils'

import { Color } from '../../../../constants'
import { Flex, Text } from '../../../../elements'
import { useTranslations } from '../../../../translate'
import { byCellFilter } from '../by-filter'
import { Cell } from '../components/Cell'
import { CellShiftsColumn } from '../components/CellShiftsColumn'
import { ResourceTitle } from '../components/ResourceTitle'
import { Row } from '../components/Row'
import { Shift } from '../components/Shift'
import { CellStatus } from '../components/useCellTheme'
import { UIShift } from '../types'
import { useSchedule } from '../useSchedule'
import { useShiftPresets as useShiftTypes } from '../useShiftPresets'
import { getShiftsInWeek } from '../utils/get-shifts-in-week'

const CELL_STATUSES = [
  'VACANT',
  'OCCUPIED',
  'SELECTED',
  'PRESENTED',
  'SELECTED',
  'REPLACING',
  'ACCEPTED',
  'REJECTED',
  'CANCELLED',
  'BOOKED',
] satisfies CellStatus[]

type PresentationCellStatus = (typeof CELL_STATUSES)[number]

type PresentationScheduleMode = 'create' | 'replacing' | 'view' | 'answer'

const getEnhancedStatus = (
  status: CellStatus,
  mode: PresentationScheduleMode,
  isSelected: boolean,
): PresentationCellStatus => {
  const isPresenting = mode === 'create'
  const isReplacing = mode === 'replacing'
  const isAnswering = mode === 'answer'

  if (status === 'OCCUPIED') return 'OCCUPIED'
  if (status === 'CANCELLED') return 'CANCELLED'

  // Handling submitting presentations
  if (status === 'VACANT') {
    if (isPresenting && isSelected) return 'SELECTED'
    return 'VACANT'
  }
  if (status === 'BOOKED') {
    if (isReplacing && isSelected) return 'REPLACING'
    return 'BOOKED'
  }

  // Handling answering to presentations
  if (status === 'PRESENTED') {
    if (isAnswering && isSelected) return 'SELECTED'
    return 'PRESENTED'
  }

  // Assuming that the status is one of the PresentationCellStatus, we want the typing so we do not
  // accidentally return a different status in the above conditions, here it does not matter
  return status as PresentationCellStatus
}

const toId = <T extends { id: string }>(value: T) => value.id

type PresentationProps = {
  mode: PresentationScheduleMode
  rows: 'NONE' | 'ENFORCED' | 'STRICT'
  selectedIds?: string[]
  onSelect?: (shiftId: Enumerable<string>) => void
  // Should ideally use the errors from useScheduleErrors but would need to update
  // PresentationAnswerShiftScheduleSection
  errors?: Record<string, SchedulingRuleViolation[]>
}

export const Presentation = (props: PresentationProps) => {
  const t = useTranslations()
  const { days, currentWeek, shifts: allShifts, shiftPopover } = useSchedule()

  // For normal schedules without specific rows
  const { presets: shiftTypes } = useShiftTypes({ shifts: allShifts })
  // For tenders with defined schedule rows
  const rows = uniq(allShifts.map(({ rowKey }) => rowKey))
    .filter((rowKey) => typeof rowKey === 'number')
    .sort((a, b) => a - b)

  const selectedRows = allShifts
    .filter(({ id }) => props.selectedIds?.includes(id))
    .map(({ rowKey }) => rowKey)
    .filter((rowKey): rowKey is number => typeof rowKey === 'number')

  if (!currentWeek) return null

  const shiftsInWeek = getShiftsInWeek(currentWeek, allShifts)

  const renderWeek = (shifts: UIShift[], rowKey: number | undefined) => {
    return days.map((day) => {
      const shiftsOnDay = groupBy(
        shifts.filter((s) => s.date === day.dateISO),
        'status',
      )

      return (
        <Cell
          key={day.dateISO}
          disabled={!day.withinRange}
          weekend={day.isWeekend}
        >
          <CellShiftsColumn>
            {CELL_STATUSES.map((status) => {
              const shifts = shiftsOnDay[status] ?? []
              if (!shifts.length) return null

              const shiftIds = shifts.map(toId)
              const shiftDetails = shifts[0]
              const isOccupied = status === 'OCCUPIED'
              const isUrgent = shifts.every((s) => s.isUrgent) && !isOccupied
              const errors = props.errors?.[day.dateISO]?.flat() ?? []

              // For strict row based tenders we only allow suppliers to present on one row
              // per presentation. As soon as a row is selected, all other rows are disabled
              const isSelectable =
                props.rows === 'STRICT' && typeof rowKey === 'number'
                  ? !selectedRows.length || selectedRows.includes(rowKey)
                  : true

              const isSelected = !!props.selectedIds?.includes(shiftDetails.id)

              const enhancedStatus = getEnhancedStatus(
                status,
                props.mode,
                isSelected,
              )

              return (
                <Shift
                  key={enhancedStatus}
                  startTime={shiftDetails.startTime}
                  endTime={shiftDetails.endTime}
                  interestedCandidates={shifts[0].interestedCandidates}
                  popoverContent={shiftPopover}
                  shiftIds={shiftIds}
                  status={isSelectable ? enhancedStatus : 'BLOCKED'}
                  unselectable={isOccupied || !isSelectable}
                  disabled={!isSelectable || isOccupied}
                  onCallBadge={shiftDetails.onCallTime}
                  tooltip={
                    errors?.length
                      ? {
                          intent: 'danger',
                          message: uniq(
                            errors.map((e) => t.$schedulingRuleViolation(e)),
                          ).join(', '),
                        }
                      : undefined
                  }
                  onClick={
                    props.onSelect && isSelectable && !isOccupied
                      ? () => props.onSelect?.(shiftIds)
                      : undefined
                  }
                  isUrgent={isUrgent}
                />
              )
            })}
          </CellShiftsColumn>
        </Cell>
      )
    })
  }

  if (props.rows === 'NONE') {
    return (
      <>
        {shiftTypes.map((shiftType) => (
          <Row key={shiftType.key}>
            <Cell isTitle rowSpan={1}>
              <Flex
                styles={{
                  p: 0.5,
                  minHeight: 52,
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: Color.DARK_GREY,
                }}
              >
                <Flex col styles={{ textAlign: 'center' }}>
                  <Text bold noWrap fontSize={14}>
                    {`${shiftType.startTime} – ${shiftType.endTime}`}
                  </Text>
                  <Text noWrap fontWeight={500} fontSize={14}>
                    {shiftType.breakTime
                      ? t.ui.break_n_min(shiftType.breakTime)
                      : t.ui.no_break()}
                  </Text>
                </Flex>
              </Flex>
            </Cell>
            {renderWeek(
              shiftsInWeek.filter(byCellFilter(shiftType)),
              undefined,
            )}
          </Row>
        ))}
      </>
    )
  }

  return (
    <>
      {rows.map((rowKey) => {
        const rowShifts = shiftsInWeek.filter(byCellFilter({ rowKey }))

        return (
          <Row key={rowKey}>
            <Cell isTitle rowSpan={1}>
              <ResourceTitle title={t.shifts.row_n(rowKey + 1)} shifts={null} />
            </Cell>
            {renderWeek(rowShifts, rowKey)}
          </Row>
        )
      })}
    </>
  )
}
